// Support & Feedback modal: members report problems, share feature ideas, or
// ask questions from anywhere in the app. Submissions are stored for admin
// review and forwarded to the support inbox.

import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { feedbackAPI } from '../api/client';
import { SUPPORT_EMAIL } from '../utils/support';
import { X, Bug, Lightbulb, HelpCircle, CheckCircle2, Loader2, Mail, Sparkles } from 'lucide-react';
import { ScreenshotPicker } from './TicketScreenshots';
import { pasteScreenshots } from '../lib/screenshots';

interface SupportFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  unreadReplies?: number;
}

const KINDS = [
  { id: 'problem', label: 'Something is broken', icon: Bug },
  { id: 'idea', label: 'I have an idea', icon: Lightbulb },
  { id: 'question', label: 'I have a question', icon: HelpCircle },
] as const;

export default function SupportFeedbackModal({ isOpen, onClose, unreadReplies = 0 }: SupportFeedbackModalProps) {
  const location = useLocation();
  const [kind, setKind] = useState<'problem' | 'idea' | 'question'>('problem');
  const [message, setMessage] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleClose = () => {
    setSubmitted(false);
    setMessage('');
    setScreenshots([]);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!message.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      await feedbackAPI.submit(kind, message.trim(), location.pathname, screenshots);
      setSubmitted(true);
      setScreenshots([]);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      const serverError = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(serverError || `Could not send your message. Please try again, or email ${SUPPORT_EMAIL}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-navy">Support & Feedback</h2>
          <button onClick={handleClose} className="p-1.5 pointer-coarse:p-2.5 pointer-coarse:-m-1 rounded-lg hover:bg-gray-100 transition" aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="mx-auto text-green-500" size={44} />
            <h3 className="mt-3 text-lg font-semibold text-navy">Sent to the team</h3>
            <p className="mt-1 text-gray-600 text-sm">
              Thank you. You can follow the reply under{' '}
              <Link to="/requests" onClick={handleClose} className="text-navy underline">Your requests</Link>.
            </p>
            <button
              onClick={handleClose}
              className="mt-5 px-5 py-2.5 bg-navy text-white rounded-lg font-medium hover:bg-navy-dark transition"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <Link
              to="/requests"
              onClick={handleClose}
              className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium transition ${
                unreadReplies > 0
                  ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                  : 'border-gray-200 text-navy hover:bg-off-white'
              }`}
            >
              <span>
                {unreadReplies > 0
                  ? `The team replied (${unreadReplies}). View your requests`
                  : 'Your requests and replies'}
              </span>
              <span aria-hidden="true">›</span>
            </Link>
            <Link
              to="/whats-new"
              onClick={handleClose}
              className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-navy hover:bg-off-white transition"
            >
              <span className="flex items-center gap-2">
                <Sparkles size={16} className="text-gold" />
                What's new: check here before sending a request
              </span>
              <span aria-hidden="true">›</span>
            </Link>
            <div className="grid grid-cols-3 gap-2">
              {KINDS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setKind(id)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition ${
                    kind === id
                      ? 'border-navy bg-navy/[0.04] text-navy'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <Icon size={20} className={kind === id ? 'text-gold' : 'text-gray-400'} />
                  {label}
                </button>
              ))}
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPaste={(e) => pasteScreenshots(e, screenshots, setScreenshots)}
              rows={5}
              placeholder={
                kind === 'problem'
                  ? 'What happened? What did you expect to happen?'
                  : kind === 'idea'
                  ? 'Describe your idea — what would make Iron Forums better?'
                  : 'What can we help you with?'
              }
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-navy/20 text-sm"
            />

            <div className="space-y-1.5">
              <ScreenshotPicker
                files={screenshots}
                onChange={setScreenshots}
                disabled={isSubmitting}
                label="Add screenshots (optional)"
              />
              <p className="text-xs text-gray-400">
                A picture of the screen helps us fix things faster. Up to 5 images, or paste one into the box above.
              </p>
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={!message.trim() || isSubmitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg font-medium hover:bg-navy-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              Send to the Iron Forums team
            </button>

            <div className="flex items-center justify-between text-xs text-gray-400 pt-1">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-center gap-1 hover:text-navy transition"
              >
                <Mail size={12} />
                {SUPPORT_EMAIL}
              </a>
              <span className="space-x-3">
                <Link to="/security" className="hover:text-navy transition underline">Security</Link>
                <Link to="/privacy" className="hover:text-navy transition underline">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-navy transition underline">Terms of Service</Link>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
